import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, Loader2, Upload, X, Check } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, SectionTitle } from "@/components/FormBits";
import { useWizardState } from "@/hooks/useWizardState";
import { api, fileUrl } from "@/lib/api";

export default function SubmitPhotos() {
  const { draftToken } = useParams();
  const navigate = useNavigate();
  const { project, loading, saving, lastSaved, update, saveNow } = useWizardState(draftToken);
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const files = (project?.files || []).filter((f) => !f.is_deleted);

  const handleFiles = async (fileList) => {
    setBusy(true);
    try {
      const accum = [...(project.files || [])];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "During works photos");
        try {
          const { data } = await api.post(`/projects/draft/${draftToken}/files`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          accum.push(data);
          update({ files: [...accum] });
        } catch (e) {
          const msg = e?.response?.data?.detail || "Upload failed";
          toast.error(typeof msg === "string" ? msg : "Upload failed");
        }
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeFile = async (path) => {
    update({ files: (project.files || []).map((f) => (f.storage_path === path ? { ...f, is_deleted: true } : f)) });
    try {
      await api.delete(`/projects/draft/${draftToken}/files/${path}`);
    } catch {}
  };

  const onSubmit = async () => {
    await saveNow();
    setSubmitting(true);
    try {
      await api.post(`/projects/draft/${draftToken}/submit`);
      localStorage.removeItem("rcw_draft_token");
      navigate(`/thank-you?p=${draftToken}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Submission failed";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !project) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 rcw-mobile-pad-bottom">
        {/* honeypot */}
        <input
          type="text"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
          aria-hidden="true"
        />
        <div className="rcw-card p-5 sm:p-8 space-y-6">
          <SectionTitle eyebrow="Photos first" hint="Fast capture from site — the marketing team will fill in the rest later.">
            Quick submit from site
          </SectionTitle>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">{files.length} photo{files.length === 1 ? "" : "s"} uploaded</div>
            <label className="rcw-btn-amber cursor-pointer" data-testid="photos-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {busy ? "Uploading…" : "Upload photos"}
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.mp4,.mov"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </label>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {files.map((f) => (
              <div key={f.storage_path} className="relative aspect-square bg-gray-100 rounded-md overflow-hidden">
                {(f.content_type || "").startsWith("image/") ? (
                  <img src={fileUrl(f.storage_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-xs text-gray-400 px-2 text-center">
                    {f.original_filename}
                  </div>
                )}
                <button
                  onClick={() => removeFile(f.storage_path)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white inline-flex items-center justify-center"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <Field label="Project / site name" required>
              <Input value={project.project_name || ""} onChange={(e) => update({ project_name: e.target.value })} data-testid="photos-project-name" placeholder="e.g. A48 Bridge, Section 3" />
            </Field>
            <Field label="Location" required>
              <Input value={project.location || ""} onChange={(e) => update({ location: e.target.value })} data-testid="photos-location" placeholder="e.g. Carmarthenshire" />
            </Field>
            <Field label="Short description" required hint="A sentence or two — the office will finish the write-up.">
              <Input value={project.one_sentence || ""} onChange={(e) => update({ one_sentence: e.target.value })} data-testid="photos-summary" placeholder="What are we looking at?" />
            </Field>
            <Field label="Client (optional)">
              <Input value={project.client_name || ""} onChange={(e) => update({ client_name: e.target.value })} data-testid="photos-client" placeholder="If you know it" />
            </Field>
            <Field label="Your name" required>
              <Input value={project.submitter_name || ""} onChange={(e) => update({ submitter_name: e.target.value })} data-testid="photos-name" />
            </Field>
            <Field label="Your email" required>
              <Input type="email" value={project.submitter_email || ""} onChange={(e) => update({ submitter_email: e.target.value })} data-testid="photos-email" />
            </Field>
          </div>

          <div className="text-xs text-gray-500">
            {saving ? "Saving…" : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : "Autosave enabled"}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-end gap-2">
          <button onClick={() => navigate("/")} className="rcw-btn-ghost text-sm">Cancel</button>
          <button onClick={onSubmit} disabled={submitting} className="rcw-btn-amber" data-testid="photos-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit draft
          </button>
        </div>
      </div>
    </Layout>
  );
}
